/**
 * EM/Lead Portal — Validation Queue page.
 * Shows draft reports submitted by SMEs (status: sme_reviewed) for EM approval.
 * Flow: Table list → Click "Review" → Preview full report → Approve / Send Back.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tabs, Tab,
  Divider, Grid, Card, CardContent, IconButton, LinearProgress, Tooltip,
} from '@mui/material';
import {
  CheckCircle, Cancel, Visibility, ArrowBack, Download,
  Star, Info, CalendarMonth,
  Slideshow, Delete,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

/* ── Types ────────────────────────────────────────────────────── */

interface QueueReport {
  id: string;
  assessment_id: string;
  organization_id: string | null;
  organization_name: string | null;
  category: string | null;
  domain: string | null;
  domain_score: number | null;
  version: number;
  status: string;
  file_name: string | null;
  has_excel: boolean;
  has_pptx: boolean;
  review_notes: string | null;
  created_at: string | null;
  submitted_by: string | null;
}

interface ReportDetail {
  id: string;
  assessment_id: string;
  organization_name: string | null;
  category: string | null;
  domain: string | null;
  domain_score: number | null;
  summary: string | null;
  recommendations: string[];
  status: string;
  review_notes: string | null;
  file_name: string | null;
  has_excel: boolean;
  has_pptx: boolean;
  submitted_by_name: string | null;
  submitted_by_email: string | null;
  created_at: string | null;
  updated_at: string | null;
  items: {
    question: string;
    score: number;
    rationale: string;
    recommendation: string;
    risk_level: string;
  }[];
}

/* ── Constants ────────────────────────────────────────────────── */

const STATUS_LABELS: Record<string, { label: string; color: 'warning' | 'success' | 'info' | 'default' }> = {
  sme_reviewed: { label: 'Pending EM Approval', color: 'warning' },
  em_approved: { label: 'Approved', color: 'success' },
  playback_scheduled: { label: 'Playback Scheduled', color: 'info' },
  draft: { label: 'Sent Back to SME', color: 'default' },
};

const DOMAIN_LABELS: Record<string, string> = {
  cloud_security_adoption: 'Cloud Security Adoption',
  azure_ai_readiness: 'Azure AI Readiness',
  m365_copilot_readiness: 'M365 Copilot Readiness',
  devops_maturity: 'DevOps Maturity',
  cloud_migration_readiness: 'Cloud Migration Readiness',
  data_platform_modernization: 'Data Platform Modernization',
};

const RISK_COLORS: Record<string, string> = {
  critical: '#d32f2f',
  high: '#f44336',
  medium: '#ff9800',
  low: '#4caf50',
};

const domainLabel = (domain: string | null) =>
  domain
    ? DOMAIN_LABELS[domain] || domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';

const scoreColor = (score: number) =>
  score >= 4 ? '#4caf50' : score >= 3 ? '#ff9800' : '#f44336';
const TAB_STATUSES = ['sme_reviewed', 'em_approved', 'playback_scheduled', 'draft'];

/* ── Component ────────────────────────────────────────────────── */

const ValidationQueue: React.FC = () => {
  const navigate = useNavigate();

  /* list state */
  const [reports, setReports] = useState<QueueReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [tab, setTab] = useState(0);

  /* detail / preview state */
  const [selectedReport, setSelectedReport] = useState<ReportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  /* approve dialog */
  const [approveDialog, setApproveDialog] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [approving, setApproving] = useState(false);

  /* reject / send-back dialog */
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [rejecting, setRejecting] = useState(false);

  /* delete state */
  const [deleteTarget, setDeleteTarget] = useState<QueueReport | null>(null);
  const [deleting, setDeleting] = useState(false);

  const tabLabels = ['Pending Approval', 'Approved', 'Playback Scheduled', 'Sent Back'];

  /* ── Fetch list ───────────────────────────────────────────── */

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`/api/sme/reports/drafts?status=${TAB_STATUSES[tab]}`);
      const data = await response.json();
      if (data.success) setReports(data.reports || []);
      else setError(data.error || 'Failed to load reports');
    } catch {
      setError('Failed to load validation queue');
    } finally {
      setLastRefreshed(new Date());
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  /* ── Fetch single report detail ───────────────────────────── */

  const openReportDetail = async (report: QueueReport) => {
    setDetailLoading(true);
    setError(null);
    try {
      const response = await authFetch(`/api/sme/reports/${report.id}/detail`);
      const data = await response.json();
      if (data.success) {
        setSelectedReport(data.report);
      } else {
        setError(data.error || 'Failed to load report detail');
      }
    } catch {
      setError('Failed to load report detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedReport(null);
  };

  /* ── Actions ──────────────────────────────────────────────── */

  const handleApprove = async () => {
    if (!selectedReport) return;
    setApproving(true);
    try {
      const response = await authFetch(`/api/sme/reports/${selectedReport.id}/em-approve`, {
        method: 'POST',
        body: JSON.stringify({ review_notes: approveNotes }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMsg(
          `Report approved${data.pptx_generated ? ' and PowerPoint generated' : ''}. Client has been notified.`
        );
        setApproveDialog(false);
        setApproveNotes('');
        setSelectedReport(null);
        fetchReports();
      } else {
        setError(data.error || 'Failed to approve report');
      }
    } catch {
      setError('Failed to approve report');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (!selectedReport) return;
    if (!rejectNotes.trim()) {
      setError('Please provide feedback for the SME.');
      return;
    }
    setRejecting(true);
    try {
      const response = await authFetch(`/api/sme/reports/${selectedReport.id}/em-reject`, {
        method: 'POST',
        body: JSON.stringify({ review_notes: rejectNotes }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setSuccessMsg('Report sent back to SME for revision. SME has been notified.');
        setRejectDialog(false);
        setRejectNotes('');
        setSelectedReport(null);
        fetchReports();
      } else {
        setError(data.error || 'Failed to send back report');
      }
    } catch {
      setError('Failed to send back report');
    } finally {
      setRejecting(false);
    }
  };

  const handleDownloadExcel = async (reportId: string, fileName: string | null) => {
    try {
      const response = await authFetch(`/api/sme/reports/${reportId}/excel`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'report.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download Excel report');
    }
  };

  const handleDownloadPptx = async (reportId: string, fileName: string | null) => {
    try {
      const response = await authFetch(`/api/sme/reports/${reportId}/pptx`);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName || 'report.pptx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download PowerPoint report');
    }
  };

  const handleDeleteReport = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/sme/reports/${deleteTarget.id}`, { method: 'DELETE' });
      if (res.ok) {
        setSuccessMsg('Report removed from queue.');
        setDeleteTarget(null);
        fetchReports();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to remove report');
      }
    } catch {
      setError('Failed to remove report');
    } finally {
      setDeleting(false);
    }
  };

  /* ── Render: Detail / Preview View ────────────────────────── */

  if (detailLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: '#C6007E' }} />
      </Box>
    );
  }

  if (selectedReport) {
    const r = selectedReport;
    const avgScore = r.domain_score ?? 0;
    const isPending = r.status === 'sme_reviewed';

    return (
      <Box>
        {/* Header bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={closeDetail}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Review Draft Report
            </Typography>
              <RefreshButton onRefresh={fetchReports} loading={loading} lastRefreshed={lastRefreshed} />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {domainLabel(r.domain)} — {r.organization_name || 'Client'}
            </Typography>
          </Box>
          {isPending && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircle />}
                onClick={() => setApproveDialog(true)}
              >
                Approve & Generate Report
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Cancel />}
                onClick={() => setRejectDialog(true)}
              >
                Send Back to SME
              </Button>
            </Box>
          )}
        </Box>

        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
            {successMsg}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="overline" color="text.secondary">Overall Score</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: scoreColor(avgScore) }}>
                  {avgScore.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">/5.0</Typography>
                <LinearProgress
                  variant="determinate"
                  value={(avgScore / 5) * 100}
                  sx={{
                    mt: 1, height: 8, borderRadius: 4,
                    '& .MuiLinearProgress-bar': { backgroundColor: scoreColor(avgScore) },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Category</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{r.category || '—'}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Domain</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{domainLabel(r.domain)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Submitted By</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {r.submitted_by_name || '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {r.submitted_by_email || ''}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Date</Typography>
                <Typography variant="body2">
                  {r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={(STATUS_LABELS[r.status] || { label: r.status }).label}
                    color={(STATUS_LABELS[r.status] || { color: 'default' }).color as any}
                    size="small"
                  />
                </Box>
                {r.has_excel && (
                  <>
                    <Divider sx={{ my: 1 }} />
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => handleDownloadExcel(r.id, r.file_name)}
                    >
                      Download Excel
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* SME Review Notes */}
        {r.review_notes && (
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#fff8e1', border: '1px solid #ffe082' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              SME Review Notes
            </Typography>
            <Typography variant="body2">{r.review_notes}</Typography>
          </Paper>
        )}

        {/* Executive Summary */}
        {r.summary && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Executive Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {r.summary}
            </Typography>
          </Paper>
        )}

        {/* Assessment Items */}
        {r.items.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Assessment Details ({r.items.length} items)
            </Typography>
            {r.items.map((item, idx) => (
              <Card
                key={idx}
                variant="outlined"
                sx={{ mb: 2, borderLeft: `4px solid ${RISK_COLORS[item.risk_level] || '#9e9e9e'}` }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                      {idx + 1}. {item.question}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
                      <Chip
                        icon={<Star sx={{ fontSize: 16 }} />}
                        label={`${item.score}/5`}
                        size="small"
                        sx={{
                          backgroundColor: `${scoreColor(item.score)}20`,
                          color: scoreColor(item.score),
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={item.risk_level.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: `${RISK_COLORS[item.risk_level] || '#9e9e9e'}20`,
                          color: RISK_COLORS[item.risk_level] || '#9e9e9e',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Rationale:</strong> {item.rationale}
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>
                      <strong>Recommendation:</strong> {item.recommendation}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Paper>
        )}

        {/* Recommendations */}
        {r.recommendations && r.recommendations.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Key Recommendations
            </Typography>
            {r.recommendations.map((rec, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Info color="primary" sx={{ mt: 0.3, fontSize: 18 }} />
                <Typography variant="body2">{rec}</Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* Bottom action bar for long reports */}
        {isPending && (
          <Paper sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, position: 'sticky', bottom: 0 }}>
            <Button variant="outlined" onClick={closeDetail}>
              Back to Queue
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => setRejectDialog(true)}
            >
              Send Back to SME
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircle />}
              onClick={() => setApproveDialog(true)}
            >
              Approve & Generate Report
            </Button>
          </Paper>
        )}

        {/* Approve Dialog */}
        <Dialog open={approveDialog} onClose={() => setApproveDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Approve Report</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Approving <strong>{domainLabel(r.domain)}</strong> for{' '}
              <strong>{r.organization_name || 'client'}</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will generate a standardized PowerPoint presentation and notify the client
              that their assessment report is ready for playback.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Review Notes (optional)"
              value={approveNotes}
              onChange={(e) => setApproveNotes(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setApproveDialog(false)} disabled={approving}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              startIcon={approving ? <CircularProgress size={16} /> : <CheckCircle />}
              onClick={handleApprove}
              disabled={approving}
            >
              {approving ? 'Generating Report...' : 'Approve & Generate Report'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Send Back Dialog */}
        <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Send Back to SME</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Sending <strong>{domainLabel(r.domain)}</strong> back to the SME for revision.
              The SME will be notified immediately.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Feedback for SME (required)"
              placeholder="Please specify what needs clarification or revision..."
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              required
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRejectDialog(false)} disabled={rejecting}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              startIcon={rejecting ? <CircularProgress size={16} /> : <Cancel />}
              onClick={handleReject}
              disabled={rejecting}
            >
              {rejecting ? 'Sending...' : 'Send Back with Feedback'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  /* ── Render: Table / List View ────────────────────────────── */

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
        Validation Queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review draft reports submitted by SMEs. Click "Review" to preview the full report
        before approving or sending back for revision.
      </Typography>

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        {tabLabels.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#C6007E' }} />
        </Box>
      ) : reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No reports in this category.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Assessment</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>SME Notes</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => {
                const st = STATUS_LABELS[r.status] || { label: r.status, color: 'default' as const };
                return (
                  <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}>
                    <TableCell onClick={() => openReportDetail(r)}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {domainLabel(r.domain)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.category || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={() => openReportDetail(r)}>
                      {r.organization_name || '—'}
                    </TableCell>
                    <TableCell onClick={() => openReportDetail(r)}>
                      {r.domain_score != null ? (
                        <Chip
                          label={`${r.domain_score.toFixed(1)}/5`}
                          size="small"
                          color={r.domain_score >= 4 ? 'success' : r.domain_score >= 3 ? 'warning' : 'error'}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell onClick={() => openReportDetail(r)}>
                      <Chip label={st.label} size="small" color={st.color} />
                    </TableCell>
                    <TableCell
                      onClick={() => openReportDetail(r)}
                      sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {r.review_notes || '—'}
                    </TableCell>
                    <TableCell onClick={() => openReportDetail(r)}>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Visibility />}
                          sx={{
                            backgroundColor: '#C6007E',
                            '&:hover': { backgroundColor: '#a10066' },
                            minWidth: 'auto',
                            whiteSpace: 'nowrap',
                          }}
                          onClick={() => openReportDetail(r)}
                        >
                          {r.status === 'sme_reviewed' ? 'Review' : 'View'}
                        </Button>
                        {r.status === 'em_approved' && r.has_pptx && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Slideshow />}
                            sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                            onClick={() => handleDownloadPptx(r.id, null)}
                          >
                            PPT
                          </Button>
                        )}
                        {r.status === 'em_approved' && (
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            startIcon={<CalendarMonth />}
                            sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                            onClick={() => navigate('/em/playback', { state: { assessmentId: r.assessment_id } })}
                          >
                            Schedule
                          </Button>
                        )}
                        {(r.status === 'em_approved' || r.status === 'playback_scheduled' || r.status === 'draft') && (
                          <Tooltip title="Remove from queue">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(r)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Remove Report from Queue</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteTarget?.domain ? domainLabel(deleteTarget.domain) : 'this report'}</strong>
            {' '}for <strong>{deleteTarget?.organization_name || 'client'}</strong> from the validation queue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteReport} disabled={deleting}>
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ValidationQueue;
